import { bind, classNames, Component, h, OmiProps, tag } from "omi";
import { Router } from "@vaadin/router";

import { AuthEvents } from "@/wc/components/nw-auth-manager/events";
import { COMMUNITY_LINK } from "@/globals/links";
import { KeyboardShortcut, onKeyboardShortcuts } from "@/utils/keys";
import { NWAvatar, NWButton, NWIcon, NWTooltip, openProModal } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { ToolbarItem } from "./components/toolbarItem";
import { NWToolbarViewManager } from "./views/view-manager";

type Controlled = {
  type: "controlled";
  show: boolean;
  withBackground?: boolean;
};

type Uncontrolled = {
  type: "uncontrolled";
  withBackground?: boolean;
};

type Props = Controlled | Uncontrolled;

@tag("nw-toolbar")
export class NWToolbar extends Component<Props> {
  static css = [tailwind];

  private keyboardShortcutCleanup?: () => void;

  private items: ToolbarItem[] = [
    {
      type: "button",
      icon: "grid",
      name: "Portfolio",
      color: "gray",
      iconOnly: false,
      active: () => false,
      onClick: () => {
        import("@/services/analyticsService").then(({ useAnalyticsService }) => {
          useAnalyticsService().trackNavItemClicked("Portfolio");
        });

        Store.app.showToolbarView(undefined);

        if (Store.app.isRouteActive("portfolio")) {
          return;
        }

        if (Store.user.isLoggedIn.value) {
          Router.go("/portfolio");
        } else {
          window.location.assign("/portfolio");
        }
      },
      withTooltip: true,
      shortcut: "o",
    },
    {
      type: "button",
      icon: "search",
      name: "Projects",
      color: "gray",
      iconOnly: false,
      onClick: () => {
        import("@/services/analyticsService").then(({ useAnalyticsService }) => {
          useAnalyticsService().trackNavItemClicked("Projects");
        });

        // If we're on portfolio, navigate to last project instead of opening search
        if (Store.app.isRouteActive("portfolio")) {
          if (Store.user.isLoggedIn.value) {
            Router.go("/");
          } else {
            window.location.assign("/");
          }
          return;
        }

        if (Store.features.hasFeature("projects.navV2")) {
          Store.app.showToolbarView("directory");
          import("@/services/analyticsService").then(({ useAnalyticsService }) => {
            useAnalyticsService().trackSidebarOpen("toolbar");
          });
        } else {
          Store.app.toggleNav(!Store.app.isNavOpen.value, "toolbar");
        }
      },
      active: () => false,
      withTooltip: true,
      shortcut: "k",
    },
    {
      type: "link",
      name: "Ask",
      icon: "message-question-circle",
      href: COMMUNITY_LINK,
      onClick: () => {
        import("@/services/analyticsService").then(({ useAnalyticsService }) => {
          useAnalyticsService().trackNavItemClicked("Stuck? Ask a question");
        });
      },
    },
  ];

  installed() {
    const shortcuts: KeyboardShortcut[] = [
      {
        keys: "cmd+o,ctrl+o",
        action: () => {
          const portfolioItem = this.items[0];
          if (portfolioItem.type === "button") {
            portfolioItem.onClick();
          }
        },
        description: "Portfolio",
      },
      {
        keys: "cmd+k,ctrl+k",
        action: () => {
          const projectsItem = this.items[1];
          if (projectsItem.type === "button") {
            projectsItem.onClick();
          }
        },
        description: "Projects",
      },
    ];

    this.keyboardShortcutCleanup = onKeyboardShortcuts(shortcuts);
  }

  uninstall() {
    this.keyboardShortcutCleanup?.();
  }

  receiveProps(props: OmiProps<Props> | Props) {
    return !!isControlled(props);
  }

  @bind
  private handleSignIn() {
    const loginEvent = new Event(AuthEvents.LoginClicked);
    dispatchEvent(loginEvent);
  }

  @bind
  private handleHomeClick() {
    if (Store.user.isLoggedIn.value) {
      // Close any open search/directory view
      Store.app.showToolbarView(undefined);
      Router.go("/");
    } else {
      window.location.assign("/");
    }
  }

  @bind
  private handleOpenPaywall() {
    openProModal("nav_explore_your_potential_button");
  }

  render() {
    const showUpgradeButton = !Store.user.isPro();

    return (
      <>
        {/* Overlay rendered above the toolbar - managed by view manager */}
        <div
          style={{
            position: "fixed",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: "calc(24px + 86px - 12px)",
            zIndex: 502,
            width: "100%",
            maxWidth: "720px",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          <NWToolbarViewManager />
        </div>

        {/* Banner rendered at root level, above overlays and toolbar */}
        {showUpgradeButton && (
          <button
            className="fixed bg-leather text-white text-sm font-bold py-1 px-5 rounded-t-2xl h-8 left-1/2 -translate-x-1/2 w-fit text-nowrap flex items-center space-x-2 hover:bg-gray-900 transition-all ease-in-out"
            style={{
              bottom: "86px",
            }}
            onClick={this.handleOpenPaywall}
          >
            <span>Explore your potential</span>
            <NWIcon name="arrow-up-right" width={20} height={20} />
          </button>
        )}

        <div
          className={classNames(
            "fixed bottom-1 left-1/2 -translate-x-1/2 h-24 flex flex-col justify-center py-4 z-[501] w-full max-w-[720px] px-4",
          )}
        >
          <div
            className={classNames(
              "pointer-events-auto transition-transform ease-in-out duration-300 flex flex-col justify-center items-center space-y-2",
            )}
          >
            <div className="relative w-full">
              {/* Three-part flex layout: logo left, nav center, avatar/login right */}
              <div
                className={classNames(
                  "pointer-events-auto bg-white py-2 px-4 border border-gray-200 rounded-3xl overflow-hidden w-full flex items-center justify-between",
                )}
                style={{
                  boxShadow:
                    "0px 12px 16px -4px var(--Colors-Effects-Shadows-shadow-lg_01, rgba(27, 25, 24, 0.08)), 0px 4px 6px -2px var(--Colors-Effects-Shadows-shadow-lg_02, rgba(27, 25, 24, 0.03)), 0px 2px 2px -1px var(--Colors-Effects-Shadows-shadow-lg_03, rgba(27, 25, 24, 0.04))",
                }}
              >
                {/* Left: Logo */}
                <div className={classNames("flex items-center flex-0 min-w-[48px] justify-start")}>
                  <img
                    src="/static/nextwork-icon.svg"
                    alt="NextWork Logo"
                    className={classNames(
                      "shrink-0 black cursor-pointer transition-transform hover:scale-110 w-10 h-10",
                    )}
                    width={40}
                    height={40}
                    onClick={this.handleHomeClick}
                  />
                </div>

                {/* Center: Navigation Items */}
                <div className="flex items-center justify-center space-x-8 mx-auto">
                  {this.items.map(item => (
                    <ToolbarItem {...item} />
                  ))}
                </div>

                {/* Right: Avatar or Login */}
                <div className={classNames("flex items-center flex-0 min-w-[48px] justify-end")}>
                  {Store.user.isLoggedIn.value ? (
                    <NWTooltip content="My profile" placement="top" delay={[400, 0]}>
                      <NWAvatar withProBorder size="lg" onClick={() => Store.app.showToolbarView("user")}></NWAvatar>
                    </NWTooltip>
                  ) : (
                    <NWButton onClick={this.handleSignIn} color="brand" size="fit">
                      Login
                    </NWButton>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

function isControlled(props: Props): props is Controlled {
  return (props as Controlled).type === "controlled";
}
